import { AppBar, Box, OutlinedInput, Paper, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { AddLoadedAssets, GetSequenceDuration, SetLoadedAssets } from "../../Redux/Reducers/redux-slice-timetool";
import { useSelector } from "react-redux";
import { useCallback, useMemo, useState } from "react";
import { assetService, cacheService } from "../../Core/Api/services";
import AutocompleteSearchCatalog from "../../Components/Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { ObjectType } from "../../Core/Api/Types/object-type";
import TimetoolMediaLibraryTable from "../../Components/Table/TimetoolMediaLibraryTable/timetool-media-library-table";
import { ObjectStatus } from "../../Core/Api/Types/object-status";
import TableSkeleton from "../../Components/Table/TableSkeleton";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { TimecodeToSec } from "../../Utils/Timecode/ReactPlayer";
import TimetoolActionsListTable from "../../Components/Table/TimetoolMediaLibraryTable/timetool-actions-list";
import { PlatformMode } from "../../Core/Api/Types/platform-mode";
import { useDispatch } from "react-redux";
import { SearchResponse } from "../../Core/Api/Types/search-response";
import { useTranslation } from "react-i18next";

const MediaLibrary: React.FC = () => {
    const { t } = useTranslation();
    const sequenceDuration = useSelector(GetSequenceDuration);
    const enqueueSnackbar = useCustomSnackbar();
    const dispatch = useDispatch();

    const [tabsValue, setTabsValue] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState("");
    const theme = useTheme();

    const handleClickTitle = useCallback((data: DbAssetObjectInfos) => {
        dispatch(SetLoadedAssets([]));
        setIsLoading(true);
        assetService.GetGraph(data.id, true, false).then((res) => {
            if (!res.success || !res.graph) {
                enqueueSnackbar(
                    res.success
                        ? t("alert:error.unableToGetAsset", { id: data.id })
                        : res.message || t("alert:error.somethingWentWrong"),
                    "error"
                );
                setIsLoading(false);
                return;
            }

            if (!res.graph.childObjects) {
                enqueueSnackbar(t("alert:warning.selectedTitleHasNoChildren"), "warning");
                setIsLoading(false);
                return;
            }

            const videos = res.graph.childObjects
                ?.flatMap((x) => x && x.childObjects!)
                ?.filter((x) => x && x.type === ObjectType.Video && x.status === ObjectStatus.Accepted);

            videos ? computeVideoList(videos || []) : setIsLoading(false);
        });
    }, []);

    const handleSearchResults = useCallback((data: SearchResponse, pattern: string) => {
        dispatch(SetLoadedAssets([]));
        const { results } = data;
        if (!results) return;
        const videos = results.filter((x) => x.type === ObjectType.Video && x.status === ObjectStatus.Accepted);
        videos ? computeVideoList(videos) : setIsLoading(false);
    }, []);

    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setTabsValue(newValue);
    }, []);

    const computeVideoList = useCallback((data: DbAssetObjectInfos[]) => {
        if (data.length === 0) setIsLoading(false);
        for (let i = 0; i < data.length; i++) {
            const frameRateMetaId = cacheService.GetMetadata().find((x) => x.tagName === "techFrameRate")?.id;
            const frameRateValue = data[i].metadata?.find((x) => x.id === frameRateMetaId)?.value || 24;
            const durationMetaId = cacheService.GetMetadata().find((x) => x.tagName === "durationSMPTE")?.id;
            const audioMixMetaId = cacheService.GetMetadata().find((x) => x.tagName === "audioMix")?.id;
            const durationValue = TimecodeToSec(
                data[i].metadata?.find((x) => x.id === durationMetaId)?.value || "00:00:00:00",
                frameRateValue
            );
            const audioMixValue = data[i].metadata?.find((x) => x.id === audioMixMetaId)?.value || null;
            const clipInfoValue = data[i].metadata?.find((x) => x.fieldName === "childClipNb")?.value;

            let metadata = GetMetadataByTagName(data[i], "audioChannels");
            let audioChannels: any[] = metadata && metadata.value ? JSON.parse(JSON.stringify(metadata?.value)) : [];

            dispatch(
                AddLoadedAssets({
                    name: data[i].name,
                    type: data[i].type,
                    classification: data[i].classification!,
                    id: `${data[i].id}`,
                    duration: durationValue,
                    src: "",
                    fps: frameRateValue,
                    titleId: data[i].parentTitleId,
                    clipNb: clipInfoValue || 0,
                    hierarchy: [data[i].id],
                    audioMix: audioMixValue,
                    audioChannels: audioChannels,
                })
            );

            if (clipInfoValue > 0) {
                dispatch(
                    AddLoadedAssets({
                        name: data[i].name,
                        type: data[i].type,
                        classification: data[i].classification!,
                        id: `placeholder-clip-${data[i].id}`,
                        duration: 0,
                        src: "",
                        fps: frameRateValue,
                        titleId: data[i].parentTitleId,
                        clipNb: 0,
                        hierarchy: [data[i].id, ""],
                        childrenFetched: false,
                    })
                );
            }
        }
        setIsLoading(false);
    }, []);

    const AppBarMemo = useMemo(
        () => (
            <AppBar
                sx={{
                    height: "50px",
                    lineHeight: "50px",
                    width: "inherit",
                    left: 0,
                    backgroundColor: theme.palette.background.paper,
                }}
                position="sticky"
            >
                <Tabs
                    value={tabsValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="inherit"
                    variant="fullWidth"
                    sx={{
                        ".MuiTabs-flexContainer": {
                            height: "50px !important",
                            lineHeight: "50px !important",
                        },
                    }}
                >
                    <Tab sx={{ height: "50px", padding: 0 }} label="Import" {...a11yProps(0)} />
                    <Tab label={t("page:tab.label.library")} {...a11yProps(1)} />
                </Tabs>
            </AppBar>
        ),
        [tabsValue]
    );

    const FirstPanelMemo = useMemo(
        () => (
            <TabPanel value={tabsValue} index={0}>
                <Stack flexWrap={"nowrap"} direction={"row"} justifyContent={"space-between"} display={"flex"} spacing={1}>
                    {cacheService.GetPlatformMode() === PlatformMode.Distribution ? (
                        <AutocompleteSearchCatalog
                            layer={ObjectLayer.Title}
                            enableSearchCallback={false}
                            onValueProposalClick={handleClickTitle}
                        />
                    ) : (
                        <AutocompleteSearchCatalog
                            layer={ObjectLayer.Asset}
                            enableSearchCallback={true}
                            onSearchBasicCallback={handleSearchResults}
                            placeholderText="Search by asset name..."
                        />
                    )}
                    <OutlinedInput
                        placeholder={t("field:placeholder.enterTextToFilterResults")}
                        onChange={(e) => setFilterText(e.target.value)}
                        value={filterText}
                        sx={{ flex: 1 }}
                    />
                </Stack>
                <Stack
                    sx={{
                        marginTop: 1.5,
                        height: "calc(100% - 64px)",
                        width: "100%",
                        maxWidth: "1200px",
                    }}
                >
                    {isLoading ? (
                        <Stack
                            sx={{ position: "relative", width: "100%" }}
                            justifyContent="center"
                            flexDirection="column"
                            padding={2}
                            alignItems="center"
                            textAlign="center"
                            height={"100%"}
                        >
                            <TableSkeleton /> <TableSkeleton />
                        </Stack>
                    ) : (
                        <TimetoolMediaLibraryTable filterText={filterText?.toLowerCase()} />
                    )}
                </Stack>
            </TabPanel>
        ),
        [isLoading, tabsValue, filterText]
    );

    const SecondPanelMemo = useMemo(
        () => (
            <TabPanel value={tabsValue} index={1}>
                <Stack
                    sx={{
                        height: "100%",
                        mt: -1.5,
                        width: "100%",
                        maxWidth: "1200px",
                    }}
                >
                    {sequenceDuration > 0 ? (
                        <TimetoolActionsListTable />
                    ) : (
                        <Stack
                            sx={{ position: "relative", width: "100%" }}
                            justifyContent="center"
                            alignItems="center"
                            textAlign="center"
                            height={"100%"}
                        >
                            <Typography variant="h5" component="h2">
                                {t("page:body.noAssetsInTimeline")}
                            </Typography>
                            <Typography mt={1} component="p" color="text.secondary">
                                {t("page:body.addContentViaImportTabInstruction")}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </TabPanel>
        ),
        [sequenceDuration, tabsValue]
    );

    return (
        <Paper
            sx={{
                scrollbarGutter: "stable",
                margin: 0.75,
                display: "flex",
                flex: 1,
                flexDirection: "column",
                position: "relative",
                overflow: "scroll",
            }}
        >
            {AppBarMemo}
            {FirstPanelMemo}
            {SecondPanelMemo}
        </Paper>
    );
};

function a11yProps(index: number) {
    return {
        id: `full-width-tab-${index}`,
        "aria-controls": `full-width-tabpanel-${index}`,
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    dir?: string;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            style={{ height: "calc(100% - 50px)" }}
            {...other}
        >
            {value === index && (
                <Box
                    sx={{
                        scrollbarGutter: "stable",
                        display: "flex",
                        maxHeight: "calc(100vh -501px)",
                        flex: 1,
                        flexDirection: "column",
                        maxWidth: "100%",
                        height: "100%",
                        paddingTop: 1.5,
                        paddingRight: 1.5,
                        paddingLeft: 1.5,
                    }}
                >
                    {children}
                </Box>
            )}
        </div>
    );
}

export default MediaLibrary;
